
// BB.1.1 Choose an Account
import moment from 'moment';
import tippy from 'tippy.js'
import { ServerTable, Event } from 'vue-tables-2';
import { select, each } from '../shared/utils/dom';
import { activityLogApi, linkedAccountsApi } from './api';
import { delimiters } from '../shared/constants';

window.moment = moment;

const el = select('[data-vue-app="web-account-activity"]');

if (el) {
  // @see https://github.com/matfish2/vue-tables-2

  // start data
  const data = {};

  // the url
  data['url'] = `/NIS/1.0/Activity`;

  // the username of the account
  data['username'] = null;

  // columns
  data['columns'] = ['endTime', 'accountId', 'emailAddress', 'activityDescription'];

  // has loaded data for the first time?
  data['hasMounted'] = false;

  // wait until Vue data has loaded, especially to and from values
  data['isReady'] = false;

  // if the table is loading data from the server
  data['loading'] = false;

  data['customerAccountFilter'] = null;

  data['customerAccounts'] = [];

  data['from'] = '';

  data['to'] = '';

  data["limit"] = 10;

  data["offset"] = 0;

  data["totalCount"] = 0;

  // start options
  const options = {
  };

  // filterable?
  options['filterable'] = false;

  options['customFilters'] = ['q'];

  options['sortable'] = ['endTime', 'accountId'];

  options['headings'] = {
    endTime: 'Date',
    accountId: 'Customer Account',
    emailAddress: 'Actor',
    activityDescription: 'Action',
  };

  // start templates
  const templates = {};

  options['templates'] = templates;

  /**
   * Template for Action
   * only template needed for tootlip functionality
   */
  templates.activityDescription = function (h, row) {
    return (
      <td>
        <span class="text-md">{row.activityDescription}<br />{row.activityStatus}<br /></span>
        <span class="text-xs">{row.activityDescription}</span>
      </td>
    );
  }

  templates.endTime = function (h, row) {
    return (
      <td>{moment(row.endTime).format('MMM D, YYYY hh:mm:ssA')}</td>
    );
  }

  /**
   * Request Function to custom handle the Request
   * @param {object} _data
   */
  options.requestFunction = function (params) {
    // to do: get to and from working
    return activityLogApi.getLatestActivityForAccount(params.q.username, params.q.customerAccountId, null, null, data.limit, data.offset)
      .catch(function (e) { // eslint-disable-line prefer-arrow-callback
        this.loading = false;
        this.dispatch('error', e);
      }.bind(this));
  };

  options.responseAdapter = function (response) { // eslint-disable-line object-shorthand
    const dates = response.data.result.map(function (x) { return new Date(x.endTime); }); // eslint-disable-line prefer-arrow-callback
    const earliestDate = new Date(Math.min.apply(null, dates));
    const latestDate = new Date(Math.max.apply(null, dates));
    data.from = moment(earliestDate).format("YYYY-MM-DD");
    data.to = moment(latestDate).format("YYYY-MM-DD");
    data.totalCount = response.data.totalCount;

    return {
      data: response.data.result,
      count: response.data.totalCount,
    };
  }

  // how many results per page?
  // set high since we're getting dated results and don't want pagination
  options.perPage = 1000;

  // set options into data
  data['options'] = options;

  Vue.use(ServerTable);

  new Vue({
    el,
    delimiters,
    data() {
      return data;
    },
    methods: {
      applyFilter() {
        Event.$emit('vue-tables.filter::q', { username: this.username, customerAccountId: this.customerAccountFilter });
      },
      impersonateUser() {
        if (this.customerAccountFilter) {
          // get account number and ldc to impersonate the user
          const accountInfo = this.customerAccounts.filter(v => v.account === this.customerAccountFilter);
          window.location = `/agent/impersonate?username=${encodeURIComponent(this.username)}&customerAccountId=${encodeURIComponent(accountInfo[0].account)}&ldc${encodeURIComponent(accountInfo[0].ldc)}`;
        } else {
          window.location = `/agent/impersonate?username=${encodeURIComponent(this.username)}`;
        }
      },
      getCustomerAccounts() {
        const self = this;
        linkedAccountsApi.getLinkedAccountsForAgent(self.username)
          .then((response) => {
            each(response, (account) => {
              self.customerAccounts.push({
                account: account.customerAccountNumber,
                ldc: account.ldc,
              });
            });
          })
          .catch(this.handleApiError)
      },
      // on previous page click, increase the offset by 10.
      prev30(e) {
        // Refresh the table with the new data, only if there are more records to be fetched.
        if (this.offset + this.limit < this.totalCount) {
          this.offset += this.limit;
          this.$refs.accountActivityTable.refresh();
        }

        e.preventDefault();
      },
      // on next page click, decrease the offset by 10 unless it is equal to zero.
      next30(e) {
        // Refresh the table with the new data, only if there are more records to be fetched.
        if (this.offset > 0) {
          this.offset -= this.limit;
          this.$refs.accountActivityTable.refresh();
        }

        e.preventDefault();
      },
    },
    mounted() {
      this.username = this.$el.dataset.username;
      this.getCustomerAccounts();

      Vue.nextTick(() => {
        // set initial 30 date range from 30 days ago until today
        const toMoment = moment().add(1, 'days');
        this.to = toMoment.format("YYYY-MM-DD");
        this.from = toMoment.subtract(30, 'days').format("YYYY-MM-DD");
        this.isReady = true;
      });
    },
    created() {
      const self = this;
      // this will remove the initial loading state and show the table
      Event.$on('vue-tables.loaded', () => {
        self.hasMounted = true;
        self.loading = false;

        // add tooltip functionality
        tippy('.table-container', {
          arrow: true,
          delay: 300, // delay for user intent
          flip: true,
          theme: 'nisource',
          target: '.tooltip',
          trigger: 'mouseenter focus',
        });
      });

      // this will remove the initial loading state and show the table
      Event.$on('vue-tables.loading', (requestData) => {
        if (!requestData.q) {
          requestData.q = { username: self.username, customerAccountId: null };
        }
        self.loading = true;
      });

      // this will remove the initial loading state and show the table
      Event.$on('vue-tables.error', () => {
        self.hasMounted = true;
        self.loading = false;
      });
    },
    computed: {
      formattedRange() {
        // format the dates so we can display them nicely on the page
        const fromFormatted = moment(this.from).format('MMM D, YYYY');
        const toFormatted = moment(this.to).format('MMM D, YYYY');
        return `${fromFormatted} - ${toFormatted}`;
      },
    },
  });
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/web-account-activity.js